import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, LanguageToggle } from '../../contexts/LanguageContext';
import NotificationBell from './NotificationBell';
import { useDemoMode } from '../demo/DemoMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function GlobalSearch({ token }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/platform/search?q=${encodeURIComponent(query)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setResults(data.results || []);
      } catch (err) { console.error(err); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, token]);

  const typeIcons = {
    student: 'fa-user-graduate', teacher: 'fa-chalkboard-teacher', class: 'fa-school',
    fee: 'fa-indian-rupee-sign', lead: 'fa-user-plus', school: 'fa-building',
  };

  return (
    <div className="relative" ref={ref} data-testid="global-search">
      <div className="flex items-center bg-white/15 rounded-lg border border-white/20 px-3 py-1.5 w-48 md:w-64 focus-within:bg-white/25 transition">
        <i className={`fa-solid ${searching ? 'fa-spinner animate-spin' : 'fa-magnifying-glass'} text-white/60 text-sm mr-2`}></i>
        <input type="text" value={query} onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query.length >= 2 && setOpen(true)}
          className="bg-transparent text-white text-sm placeholder-white/50 outline-none w-full"
          placeholder="Search..." data-testid="global-search-input" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full mt-2 left-0 w-80 bg-white rounded-xl shadow-2xl border z-50 max-h-80 overflow-y-auto" data-testid="search-results">
          {results.slice(0, 10).map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b last:border-0" onClick={() => setOpen(false)}>
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                <i className={`fa-solid ${typeIcons[r.type] || 'fa-circle'} text-orange-500 text-sm`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{r.name || r.title}</p>
                <p className="text-xs text-gray-400">{r.type} {r.detail ? `· ${r.detail}` : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Header Component - Main app header with branding and user info
 */
export function Header({ user, onLogout, token }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const { state: demoState } = useDemoMode();
  const isDemo = !!demoState?.is_demo;

  const triggerReplayTour = () => {
    try { localStorage.removeItem(`welcomeTour:dismissed:${user?.id}`); } catch { /* */ }
    try { localStorage.removeItem(`navTour:done:${user?.id}`); } catch { /* */ }
    // School owners get the full element-highlighting nav tour; demo users keep
    // their welcome modal too.
    if (user?.role === 'school_admin') {
      window.dispatchEvent(new Event('school-tour:start'));
    } else {
      window.dispatchEvent(new Event('welcome-tour:replay'));
    }
  };
  
  const roleLabels = { 
    platform_admin: language === 'hi' ? 'प्लेटफ़ॉर्म एडमिन' : 'Platform Admin', 
    school_admin: language === 'hi' ? 'स्कूल एडमिन' : 'School Admin', 
    teacher: language === 'hi' ? 'शिक्षक' : 'Teacher', 
    parent: language === 'hi' ? 'अभिभावक' : 'Parent', 
    student: language === 'hi' ? 'विद्यार्थी' : 'Student' 
  };

  // Role-based home navigation
  const goHome = () => {
    const roleHome = {
      platform_admin: '/dashboard',
      school_admin: '/dashboard',
      teacher: '/dashboard',
      parent: '/parent-dashboard',
      student: '/dashboard',
    };
    navigate(roleHome[user?.role] || '/dashboard');
  };

  return (
    <header className="bg-gradient-to-r from-orange-600 to-amber-500 text-white shadow-lg" data-testid="app-header">
      <div className="w-full px-3 sm:px-4 py-2 flex justify-between items-center gap-2">
        {/* Left side - Logo (clickable → home) */}
        <button
          type="button"
          onClick={goHome}
          className="flex items-center gap-2 shrink-0 cursor-pointer hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg"
          data-testid="header-logo-home"
          aria-label={language === 'hi' ? 'होम पेज पर जाएं' : 'Go to home'}
        >
          <img src="/shiksha-mitra-logo.png" alt="शिक्षा मित्र" className="h-9 w-9 sm:h-12 sm:w-12 rounded-lg object-contain" />
          <div className="hidden sm:block text-left">
            <h1 className="text-xl font-bold leading-tight">शिक्षा मित्र</h1>
            <p className="text-orange-100 text-xs">{roleLabels[user.role] || user.role}</p>
          </div>
        </button>
        
        {/* Center - Search (desktop) */}
        <div className="hidden md:block flex-1 max-w-sm mx-4">
          <GlobalSearch token={token} />
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Search icon (mobile) */}
          <button onClick={() => setShowSearch(!showSearch)} className="md:hidden p-2 hover:bg-white/20 rounded-lg transition" data-testid="mobile-search-toggle">
            <i className={`fa-solid ${showSearch ? 'fa-times' : 'fa-magnifying-glass'} text-sm`}></i>
          </button>
          <LanguageToggle />
          {(isDemo || user?.role === 'school_admin') && (
            <button
              onClick={() => window.dispatchEvent(new Event('diksha-demo:start'))}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-amber-50 text-orange-600 rounded-lg text-xs font-bold border border-white shadow-md hover:shadow-lg ring-2 ring-white/50 transition-all hover:scale-105"
              data-testid="diksha-demo-btn"
              title={language === 'hi' ? 'दीक्षा Guided Demo' : 'Diksha Guided Demo'}
            >
              <i className="fa-solid fa-wand-magic-sparkles text-fuchsia-500"></i>
              <span className="hidden md:inline">{language === 'hi' ? 'Diksha Demo' : 'Diksha Demo'}</span>
            </button>
          )}
          {(isDemo || user?.role === 'school_admin') && (
            <button
              onClick={triggerReplayTour}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg transition text-xs font-medium border border-white/20"
              data-testid="replay-tour-btn"
              title={language === 'hi' ? 'Tour फिर से चलाएं' : 'Replay Tour'}
            >
              <i className="fa-solid fa-circle-play text-amber-200"></i>
              <span className="hidden md:inline">{language === 'hi' ? 'Tour फिर से' : 'Replay Tour'}</span>
            </button>
          )}
          <NotificationBell token={token} />
          <div className="hidden lg:flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
            <span className="text-white text-sm font-medium">{user.name}</span>
          </div>
          <button onClick={onLogout} className="px-2.5 sm:px-4 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition text-xs sm:text-sm font-medium" data-testid="logout-btn">
            {t('logout')}
          </button>
        </div>
      </div>
      {/* Mobile Search Bar (expandable) */}
      {showSearch && (
        <div className="md:hidden px-3 pb-2">
          <GlobalSearch token={token} />
        </div>
      )}
    </header>
  );
}

export default Header;
